import type { MeasurementStrategyContext, PdpMeasurementStrategy } from './PdpMeasurementStrategy';

const GALLERY_CONTAINER_SELECTOR = '[data-gallery-slider-container="true"]';

function findGalleryContainer(document: Document): Element | null {
  return (
    document.querySelector(`.gallery-slider ${GALLERY_CONTAINER_SELECTOR}`) ??
    document.querySelector(`.product-detail-media ${GALLERY_CONTAINER_SELECTOR}`) ??
    document.querySelector(GALLERY_CONTAINER_SELECTOR)
  );
}

function readActiveSlideItem(container: Element): Element | null {
  return container.querySelector('.tns-item.tns-slide-active');
}

/**
 * Standard Shopware tiny-slider gallery: maps `image.viewed` to real slide changes via `tns-slide-active`
 * so arrows, swipe, and async slider init still count—thumbnail-only heuristics miss those paths.
 *
 * Plugin coverage: yes when the extension still uses tiny-slider’s active slide class (many “customize
 * gallery” / large-image Shopware Store plugins do). Not for full replacements that remove `tns-*`
 * (e.g. some masonry-only layouts) — add another strategy or extend detection for those DOM models.
 */
export class GallerySlideViewedStrategy implements PdpMeasurementStrategy {
  private ctx: MeasurementStrategyContext | null = null;

  private container: Element | null = null;

  private lastActiveSlide: Element | null = null;

  private mutationObserver: MutationObserver | null = null;

  attach(ctx: MeasurementStrategyContext): void {
    this.ctx = ctx;
    const { store, document: doc } = ctx;

    this.container = findGalleryContainer(doc);
    if (!this.container) {
      return;
    }

    this.lastActiveSlide = readActiveSlideItem(this.container);

    this.mutationObserver = new MutationObserver(() => {
      if (!this.container) {
        return;
      }
      const next = readActiveSlideItem(this.container);
      if (next != null && next !== this.lastActiveSlide) {
        store.incrementImageViewed();
      }
      if (next != null) {
        this.lastActiveSlide = next;
      }
    });

    this.mutationObserver.observe(this.container, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
    });
  }

  dispose(): void {
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;
    this.container = null;
    this.lastActiveSlide = null;
    this.ctx = null;
  }
}
